// src/pages/PublicAkuttPortal.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getDisplayName } from "../utils/displayName";
import { useSideTittel } from "../components/tilgjengelighet";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

function fmtDT(d) {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const diffMin = Math.floor((now - dt) / 60000);
  if (diffMin < 1) return "Nå nettopp";
  if (diffMin < 60) return `${diffMin} min siden`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} t siden`;
  return dt.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function PublicAkuttPortal() {
  const { token } = useParams();

  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tekst, setTekst] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState("");

  const bottomRef = useRef(null);

  useSideTittel(`Akuttforespørsel – ${getDisplayName()}`);

  async function fetchThread(silent = false) {
    try {
      const r = await fetch(`${API_BASE_URL}/akutt/portal/${token}`);
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(r.status === 410 && b.message ? b.message : "");
      }
      const data = await r.json();
      setThread(data);
      setErr("");
    } catch (e) {
      if (!silent) setErr(e.message || "Forespørselen ble ikke funnet.");
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    if (!token) return;
    fetchThread();
    const id = setInterval(() => fetchThread(true), 15_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  async function sendMelding() {
    const t = tekst.trim();
    if (!t || sending) return;
    setSending(true);
    setSendErr("");
    try {
      const r = await fetch(`${API_BASE_URL}/akutt/portal/${token}/melding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tekst: t }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Kunne ikke sende melding.");
      }
      const data = await r.json();
      setThread(data);
      setTekst("");
    } catch (e) {
      setSendErr(e.message || "Kunne ikke sende melding. Prøv igjen.");
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMelding();
    }
  }

  if (loading) {
    return <div className="p-4 text-gray-600 text-sm" role="status">Laster forespørsel…</div>;
  }

  if (err || !thread) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-1">Akuttforespørsel</h1>
        <p className="text-gray-700">Forespørselen ble ikke funnet.</p>
        <p className="text-sm text-gray-600 mt-1">{err || "Denne lenken er ikke gyldig."}</p>
      </div>
    );
  }

  const bikeInfo = [thread.merke, thread.modell].filter(Boolean).join(" ");
  const messages = thread.messages || [];
  const erStengt = !!thread.erStengt;

  return (
    <div className="mx-auto w-full max-w-lg p-4">
      {/* Header */}
      <h1 className="text-xl font-semibold mb-0.5">Akuttforespørsel</h1>
      <p className="text-sm text-gray-600 mb-4">
        {thread.navn}
        {bikeInfo && <> · <span><span aria-hidden="true">🚲 </span>{bikeInfo}</span></>}
        {" · "}
        <span className="text-gray-600">{getDisplayName()} hverdager 10–16</span>
      </p>

      {thread.godkjent && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 mb-3 text-sm text-green-800">
          Vi har booket inn service til deg. Du vil motta egen SMS med lenke til bookingen.
        </div>
      )}
      {thread.avvist && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 mb-3 text-sm text-gray-700">
          Denne henvendelsen er avsluttet uten booking.
        </div>
      )}

      {/* Meldingstråd */}
      <div className="border rounded-lg p-3 mb-3 space-y-2">
        {messages.length === 0 && (
          <p className="text-sm text-gray-600">Ingen meldinger ennå.</p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                m.from === "customer"
                  ? "bg-red-700 text-white"
                  : "bg-gray-100 text-gray-900"
              }`}
            >
              <p className="leading-snug whitespace-pre-wrap">{m.tekst}</p>
              <p className={`text-[10px] mt-1 ${
                m.from === "customer" ? "text-white text-right" : "text-gray-600"
              }`}>
                {m.from === "customer" ? thread.navn : (m.av || getDisplayName())}
                {" · "}
                {fmtDT(m.createdAt)}
              </p>
            </div>
          </div>
        ))}

        {!thread.besvart && messages.length > 0 && messages[messages.length - 1].from === "customer" && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600 justify-center pt-1">
            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse motion-reduce:animate-none" />
            Venter på svar fra mekaniker…
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Innskrivingsfelt eller stengt-melding */}
      {erStengt || thread.godkjent || thread.avvist ? (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-5 text-center space-y-2">
          <p className="text-sm font-semibold text-gray-700">Denne henvendelsen er avsluttet</p>
          <p className="text-sm text-gray-600">
            Vil du ta kontakt igjen? Ring oss eller start en ny henvendelse på nettsiden.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sendErr && <p className="text-xs text-red-700" role="alert">{sendErr}</p>}
          <label htmlFor="portal-melding" className="block text-xs font-medium text-gray-700">
            Din melding
          </label>
          <textarea
            id="portal-melding"
            rows={3}
            value={tekst}
            onChange={(e) => setTekst(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Skriv en melding… (Enter sender)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-700"
          />
          <button
            type="button"
            onClick={sendMelding}
            disabled={!tekst.trim() || sending}
            className="px-4 py-2 bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white font-medium rounded-lg transition text-sm"
          >
            {sending ? "Sender…" : "Send melding"}
          </button>
        </div>
      )}
    </div>
  );
}
